import React, { useContext, memo, FC } from 'react';
import {
  makeStyles, mergeClasses, Button, Label, tokens, shorthands,
} from '@fluentui/react-components';
import { I18nContext, I18nModel } from '@bingads-webui/copilot-i18n';
import {
  Chat16Regular, Dismiss16Regular, PersonSupport16Regular, ArrowClockwise16Regular,
} from '@fluentui/react-icons';
import { IHeaderProps } from './Header';

const useHeaderStyles = makeStyles({
  root: {
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalS,
    borderBottomColor: '#0000001F',
    borderBottomStyle: 'solid',
    borderBottomWidth: '1px',
    pointerEvents: 'all',
  },
  leftPanel: {
    ...shorthands.flex(1),
    display: 'flex',
    alignItems: 'center',
  },
  title: {
    marginLeft: tokens.spacingHorizontalXS,
    marginRight: tokens.spacingHorizontalS,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: tokens.fontSizeBase400,
    fontSize: tokens.fontSizeBase200,
    marginTop: 0,
    marginBottom: 0,
  },
  label: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase100,
    backgroundColor: tokens.colorNeutralBackground4,
    lineHeight: tokens.lineHeightBase200,
    verticalAlign: 'middle',
    paddingLeft: tokens.spacingHorizontalSNudge,
    paddingRight: tokens.spacingHorizontalSNudge,
    'border-radius': tokens.borderRadiusLarge,
  },
});

export const BingChatHeader: FC<IHeaderProps> = memo(({
  showPreviewTag, showSupportButton, showRefreshButton, children, title, onSupportClick, onRefresh, onClose,
}) => {
  const styles = useHeaderStyles();
  const i18n = useContext(I18nContext) as I18nModel;
  const closeBtnLabel = i18n.getString(_TL_('Close'));
  const refreshBtnLabel = i18n.getString(_TL_('Start over'));
  const supportBtnLabel = i18n.getString(_TL_('Connect to support'));

  // Please do not create a separate file for SVG icon
  return (
    <div className={mergeClasses(styles.root, 'ads-copilot-header')}>
      <div className={styles.leftPanel}>
        {children || (
          <>
            <svg width="0" height="0">
              <linearGradient id="blue-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop stopColor="#18C1ED" offset="0%" />
                <stop stopColor="#3495EA" offset="50%" />
                <stop stopColor="#2161EF" offset="100%" />
              </linearGradient>
            </svg>
            <Chat16Regular primaryFill="url(#blue-gradient)" />
            <h3 className={mergeClasses(styles.title, 'title')}>{title || i18n.getString(_TL_('Chat'))}</h3>
            {showPreviewTag && <Label className={mergeClasses(styles.label, 'label')} size="small" weight="semibold">{i18n.getString(_TL_('PREVIEW'))}</Label>}
          </>
        )}
      </div>
      {showSupportButton && <Button className="support-button" onClick={onSupportClick} title={supportBtnLabel} aria-label={supportBtnLabel} icon={<PersonSupport16Regular />} appearance="transparent" />}
      {showRefreshButton && <Button className="refresh-button" onClick={onRefresh} title={refreshBtnLabel} aria-label={refreshBtnLabel} icon={<ArrowClockwise16Regular />} appearance="transparent" />}
      <Button className="close-button" title={closeBtnLabel} aria-label={closeBtnLabel} icon={<Dismiss16Regular />} appearance="transparent" onClick={onClose} />
    </div>
  );
});

BingChatHeader.defaultProps = {
  onClose: undefined,
  showPreviewTag: true,
  showSupportButton: true,
  showRefreshButton: true,
  onRefresh: undefined,
  children: undefined,
  title: undefined,
};
